import { useEffect, useState } from 'react';
import { X, ArrowsClockwise } from '@phosphor-icons/react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import { PURPOSES } from './purposes';

const CONN_OPTS = ['有線', '無線'];

// Inline side drawer (NO portal, NO backdrop-blur — Edge hit-testing rule).
export default function IpEditDrawer({ entry, onClose, onSave, onRelease, onOmadaWrite }) {
  const open = !!entry;
  const [form, setForm] = useState({});

  useEffect(() => { if (entry) setForm({ ...entry }); }, [entry]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const isOmada = form.source === 'omada' && form.mac_address;

  return (
    <>
      <div
        onClick={onClose}
        data-testid="ip-drawer-overlay"
        className={`fixed inset-0 z-[55] bg-zinc-900/70 transition-opacity duration-200 ${open ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
      />
      <aside
        data-testid="ip-edit-drawer"
        className={`fixed top-0 right-0 z-[60] h-full w-full max-w-md bg-white shadow-2xl flex flex-col transition-transform duration-300 ease-out ${open ? 'translate-x-0' : 'translate-x-full'}`}
      >
        {open && (
          <>
            <header className="flex items-center justify-between px-5 py-4 border-b border-slate-200 shrink-0">
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-wider text-zinc-400">IP割当の編集</p>
                <p className="font-mono text-lg font-bold text-zinc-900">{form.ip_address}</p>
              </div>
              <button type="button" onClick={onClose} data-testid="ip-drawer-close" className="w-9 h-9 inline-flex items-center justify-center rounded-lg text-zinc-500 hover:bg-zinc-100">
                <X size={18} />
              </button>
            </header>

            <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
              <div>
                <Label className="text-sm font-semibold text-zinc-700">接続</Label>
                <div className="mt-1.5 flex gap-2">
                  {CONN_OPTS.map((c) => (
                    <button
                      key={c}
                      type="button"
                      data-testid={`conn-opt-${c}`}
                      onClick={() => set('connection', form.connection === c ? null : c)}
                      className={`px-4 h-9 rounded-lg text-sm font-medium border transition-colors ${
                        form.connection === c ? 'bg-teal-700 text-white border-teal-700' : 'bg-white text-zinc-700 border-slate-300 hover:border-slate-400'
                      }`}
                    >
                      {c}
                    </button>
                  ))}
                </div>
              </div>

              {[
                ['user', '使用者'],
                ['device_name', 'デバイス名'],
                ['mac_address', 'MACアドレス'],
                ['note', 'ノート'],
              ].map(([k, label]) => (
                <div key={k}>
                  <Label className="text-sm font-semibold text-zinc-700">{label}</Label>
                  <Input
                    data-testid={`drawer-field-${k}`}
                    value={form[k] || ''}
                    onChange={(e) => set(k, e.target.value)}
                    className={`mt-1 h-10 rounded-lg border-slate-300 ${k === 'mac_address' ? 'font-mono' : ''}`}
                  />
                </div>
              ))}

              <div>
                <Label className="text-sm font-semibold text-zinc-700">用途</Label>
                <div className="mt-1.5 flex flex-wrap gap-1.5" data-testid="drawer-purpose-group">
                  {PURPOSES.map((p) => {
                    const sel = form.purpose === p.label;
                    return (
                      <button
                        key={p.label}
                        type="button"
                        data-testid={`purpose-opt-${p.label}`}
                        onClick={() => set('purpose', sel ? null : p.label)}
                        className={`inline-flex items-center gap-1.5 px-2.5 h-8 rounded-lg text-xs font-medium border transition-all ${sel ? 'text-white shadow-sm' : 'bg-white text-zinc-700 border-slate-300 hover:border-slate-400'}`}
                        style={sel ? { backgroundColor: p.color, borderColor: p.color } : undefined}
                      >
                        <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: sel ? '#ffffff' : p.color }} />
                        {p.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="flex items-center justify-between rounded-lg border border-slate-200 px-3 py-2.5">
                <div>
                  <Label className="text-sm font-semibold text-zinc-700">認証フリー</Label>
                  <p className="text-[11px] text-zinc-400">Omadaで手動設定した端末に印を付けます（MAC単位）</p>
                </div>
                <Switch data-testid="drawer-auth-free" checked={!!form.auth_free} onCheckedChange={(v) => set('auth_free', v)} />
              </div>

              <div className="flex items-center justify-between rounded-lg border border-slate-200 px-3 py-2.5">
                <div>
                  <Label className="text-sm font-semibold text-zinc-700">固定（自動解放しない）</Label>
                  <p className="text-[11px] text-zinc-400">
                    スイッチ・APなどOmadaのクライアント一覧に出ない機器に印を付けます。
                    30日ルールの自動解放から除外されます。
                  </p>
                </div>
                <Switch data-testid="drawer-no-auto-release" checked={!!form.no_auto_release}
                  onCheckedChange={(v) => set('no_auto_release', v)} />
              </div>

              {isOmada && (
                <div className="rounded-lg bg-blue-50 border border-blue-100 p-3 space-y-2" data-testid="omada-write-section">
                  <p className="text-xs text-blue-700 font-semibold inline-flex items-center gap-1.5">
                    <ArrowsClockwise size={14} /> Omada管理デバイス — 変更をコントローラーへ書き戻せます
                  </p>
                  <Label className="text-sm font-semibold text-zinc-700">固定IP（DHCP予約）</Label>
                  <Input
                    data-testid="edit-fixed-ip"
                    value={form.fixed_ip || ''}
                    onChange={(e) => set('fixed_ip', e.target.value)}
                    placeholder="例: 192.168.0.50"
                    className="h-10 rounded-lg border-slate-300 font-mono"
                  />
                  <div className="flex flex-wrap gap-2 pt-1">
                    <Button size="sm" data-testid="omada-write-btn" onClick={() => onOmadaWrite(form, false)} className="bg-teal-700 hover:bg-teal-800">
                      名前・固定IPをOmadaに反映
                    </Button>
                    <Button size="sm" variant="outline" data-testid="omada-clear-fixed-btn" onClick={() => onOmadaWrite(form, true)}>
                      固定IP解除
                    </Button>
                  </div>
                  <p className="text-[11px] text-zinc-400">※ 反映はエージェントの次回同期時（最大10分）に実行されます。</p>
                </div>
              )}
            </div>

            <footer className="px-5 py-3 border-t border-slate-200 flex items-center justify-between gap-2 shrink-0">
              {form.status === 'assigned' ? (
                <Button variant="outline" data-testid="drawer-release-btn" onClick={() => onRelease(form)} className="text-zinc-600">
                  IPを解放
                </Button>
              ) : <span />}
              <div className="flex gap-2">
                <Button variant="outline" onClick={onClose}>キャンセル</Button>
                <Button data-testid="drawer-save-btn" onClick={() => onSave(form)} className="bg-teal-700 hover:bg-teal-800">保存</Button>
              </div>
            </footer>
          </>
        )}
      </aside>
    </>
  );
}
